/* Los iconos de FiBOT, dibujados acá adentro.
 *
 * POR QUÉ NO SON ARCHIVOS. Los que había eran PNG heredados de CantApp
 * (`assets/logos/blacks/LOGO_*.png`), y traían tres problemas que no se
 * arreglan cambiando el dibujo:
 *
 *   · Eran NEGROS sobre transparente, así que en tema oscuro desaparecían. Por
 *     eso existía una carpeta `whites/` con los mismos iconos pintados de otro
 *     color: dos archivos para el mismo concepto, y el que agregaba uno nuevo
 *     tenía que acordarse de hacer las dos versiones.
 *   · Eran rasters de 32px. En una pantalla retina se ven blandos, y no hay
 *     tamaño intermedio: o se pixela o se agranda el archivo.
 *   · Cada uno era un pedido más al servidor y un archivo más en el bundle.
 *
 * Un SVG en línea con `currentColor` resuelve los tres de una: hereda el color
 * del texto —así que el tema lo maneja solo, sin una segunda carpeta—, es
 * nítido en cualquier tamaño, y no viaja por separado.
 *
 * TODOS EN LA MISMA GRILLA DE 24, con el mismo grosor de trazo y las mismas
 * puntas redondeadas. Es lo que hace que un set se lea como un set: si uno
 * viene relleno y otro de contorno, la fila de baldosas parece armada con lo
 * que había a mano — que es exactamente como se veía antes.
 *
 * Para agregar uno: una entrada más acá, con `fill="none"` y
 * `stroke="currentColor"`. No hay que tocar nada más.
 */

const TRAZO = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

/* El dibujo de cada uno. La clave es el CONCEPTO y no el dibujo —"camion" y no
 * "truck-side"— para que cambiar el trazo mañana no obligue a renombrar los
 * veintitrés lugares que lo usan. */
const DIBUJOS = {
  camion: (
    <>
      <path d="M2 7h11v9H2z" />
      <path d="M13 10h4l4 3.5V16h-8z" />
      <circle cx="6.5" cy="18" r="1.8" />
      <circle cx="17" cy="18" r="1.8" />
    </>
  ),
  furgon: (
    <>
      <path d="M3 6h13v11H3z" />
      <path d="M16 9h3l2 3v5h-5z" />
      <path d="M6 6v11M10 6v11" />
    </>
  ),
  auto: (
    <>
      <path d="M4 15l1.6-4.6A2 2 0 0 1 7.5 9h9a2 2 0 0 1 1.9 1.4L20 15v3h-3v-1.5H7V18H4z" />
      <circle cx="7.5" cy="15" r="1" />
      <circle cx="16.5" cy="15" r="1" />
    </>
  ),
  lista: (
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <path d="M4 5.6l1.1 1.2L7 4.8M4 11.6l1.1 1.2L7 10.8M4 17.6l1.1 1.2L7 16.8" />
    </>
  ),
  stock: (
    <>
      <path d="M12 3l8 4.2v9.6L12 21l-8-4.2V7.2z" />
      <path d="M4 7.2l8 4.2 8-4.2M12 11.4V21" />
    </>
  ),
  proveedor: (
    <>
      <path d="M3 17.5c1.6-1 3.2-1 4.8 0l1.4.9c1 .6 2.2.6 3.2 0l4.8-3a2 2 0 0 0-2-3.4l-3.4 2" />
      <path d="M7 12V5.5h10V12" />
      <path d="M11 5.5v3h2v-3" />
    </>
  ),
  personas: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M16 6.2a3 3 0 0 1 0 5.6M17.5 19a5.6 5.6 0 0 0-2-4.3" />
    </>
  ),
  herramienta: (
    <>
      <path d="M14.7 6.3a3.7 3.7 0 0 0 4.8 4.8l-7.6 7.6a2.4 2.4 0 0 1-3.4-3.4z" />
      <path d="M19.5 11.1L21 6.6 18.4 4 14 5.5" />
    </>
  ),
  llave: (
    <>
      <circle cx="7.5" cy="14.5" r="3.5" />
      <path d="M10 12L19 3M16.5 5.5l2 2M14 8l2 2" />
    </>
  ),
  porteria: (
    <>
      <path d="M12 3l7 3v5.5c0 4-3 7.4-7 8.5-4-1.1-7-4.5-7-8.5V6z" />
      <path d="M9.3 12.2l1.9 1.9 3.7-3.9" />
    </>
  ),
  satelital: (
    <>
      <circle cx="12" cy="12" r="2.4" />
      <path d="M6.6 6.6a7.6 7.6 0 0 0 0 10.8M17.4 6.6a7.6 7.6 0 0 1 0 10.8" />
      <path d="M3.6 3.6a11.8 11.8 0 0 0 0 16.8M20.4 3.6a11.8 11.8 0 0 1 0 16.8" />
    </>
  ),
  /* ── Las siete del taller de PDFs ────────────────────────────────────────
   *
   * Éstas no son iconos de concepto como las de arriba: MUESTRAN LA OPERACIÓN.
   * Y no es adorno. «Separar» y «Borrar páginas» son la misma frase dada
   * vuelta —una se queda con las páginas que decís, la otra se queda con las
   * demás— y con el título solo se elige mal; el dibujo de una hoja que se
   * parte en dos contra el de una hoja tachada en el medio se distinguen sin
   * leer. Lo mismo con el par «Fotos a PDF» / «PDF a JPG», que difieren
   * únicamente en hacia dónde va la flecha: por eso los nombres son
   * `foto_a_hoja` y `hoja_a_foto` y no `fotos` y `jpg`, que se confunden acá
   * adentro igual que en la pantalla.
   *
   * La flecha es siempre la misma y siempre apunta a la derecha: lo que entra
   * está de un lado y lo que sale del otro. */
  unir: (
    <>
      <path d="M3 3.5h5.5v7H3zM3 13.5h5.5v7H3z" />
      <path d="M15.5 6H21v12h-5.5z" />
      <path d="M10.4 12h3.3M12 10.3 13.7 12 12 13.7" />
    </>
  ),
  separar: (
    <>
      <path d="M3 6h5.5v12H3z" />
      <path d="M15.5 3.5H21v7h-5.5zM15.5 13.5H21v7h-5.5z" />
      <path d="M10.4 12h3.3M12 10.3 13.7 12 12 13.7" />
    </>
  ),
  quitar_hoja: (
    <>
      <path d="M2.5 5.5h5v13h-5zM16.5 5.5h5v13h-5z" />
      <path d="M9.5 5.5h5v13h-5z" />
      <path d="m10.7 10.2 2.6 3.6M13.3 10.2l-2.6 3.6" />
    </>
  ),
  rotar: (
    <>
      <path d="M4 11h11v9.5H4z" />
      <path d="M8.5 8.6a5.6 5.6 0 0 1 10.9 0" />
      <path d="M19.4 4.9v3.7h-3.7" />
    </>
  ),
  foto_a_hoja: (
    <>
      <path d="M2.5 6h9v12h-9z" />
      <circle cx="5.4" cy="9.4" r="1.1" />
      <path d="m2.5 15.6 3.2-3.1 2.3 2.2 1.4-1.3 2.1 2" />
      <path d="M13.2 12h3.3M14.8 10.3 16.5 12l-1.7 1.7" />
      <path d="M18 6h4v12h-4z" />
    </>
  ),
  comprimir: (
    <>
      <path d="M6 8.5h12v7H6z" />
      <path d="M12 2.5v4M10.2 4.9 12 6.7l1.8-1.8" />
      <path d="M12 21.5v-4M10.2 19.1 12 17.3l1.8 1.8" />
    </>
  ),
  hoja_a_foto: (
    <>
      <path d="M2 6h4v12H2z" />
      <path d="M7.5 12h3.3M9.1 10.3 10.8 12l-1.7 1.7" />
      <path d="M12.5 6h9v12h-9z" />
      <circle cx="15.4" cy="9.4" r="1.1" />
      <path d="m12.5 15.6 3.2-3.1 2.3 2.2 1.4-1.3 2.1 2" />
    </>
  ),
  /* Hacer buscable: una hoja con renglones y una lupa encima. La lupa es lo
     que dice «buscar» sin leer el rótulo, y los renglones que lo buscado está
     ADENTRO del documento — no es buscar un archivo, es buscar en él. */
  buscar_texto: (
    <>
      <path d="M4.5 2.5h11l4 4v15h-15z" />
      <path d="M15.5 2.5v4h4" />
      <path d="M7.5 10h6M7.5 13h4" />
      <circle cx="14.2" cy="16.2" r="3.1" />
      <path d="m16.5 18.5 2.8 2.8" />
    </>
  ),
  mas: (
    <>
      <path d="M12 5v14M5 12h14" />
    </>
  ),
};

export const ICONOS = Object.keys(DIBUJOS);

/* `nombre` desconocido devuelve null y NO un icono de reemplazo. Un cuadrado
 * gris en una baldosa se lee como una función que todavía no está lista; un
 * hueco se ve como lo que es —falta el icono— y se arregla. */
const Icono = ({ nombre, tam = 22, className = "", titulo = "" }) => {
  const dibujo = DIBUJOS[nombre];
  if (!dibujo) return null;
  return (
    <svg
      className={`icono ${className}`.trim()}
      width={tam}
      height={tam}
      viewBox="0 0 24 24"
      role={titulo ? "img" : "presentation"}
      aria-hidden={titulo ? undefined : "true"}
      aria-label={titulo || undefined}
      {...TRAZO}
    >
      {titulo && <title>{titulo}</title>}
      {dibujo}
    </svg>
  );
};

export default Icono;
